import * as z from "zod";
import { ExternalRmaIntegration } from "./integrations/returns/Integrations";

/**
 * Integrations whose records carry a location that a POS store location can be
 * mapped onto. Adding one here is what surfaces it in the POS settings mapping
 * UI; the integration still has to read the mapping when it builds its records.
 */
export const POS_EXTERNAL_LOCATION_INTEGRATIONS = [
  ExternalRmaIntegration.NETSUITE,
] as const;

export type PosExternalLocationIntegration =
  (typeof POS_EXTERNAL_LOCATION_INTEGRATIONS)[number];

export const posExternalLocationMappingSchema = z.object({
  posLocationId: z.string(),
  integration: z.enum(POS_EXTERNAL_LOCATION_INTEGRATIONS),
  externalLocationId: z.string(),
});

export interface PosExternalLocationMapping extends z.infer<
  typeof posExternalLocationMappingSchema
> {}

/**
 * Shape as stored: the mapping subdocument's enum is the full integration list,
 * so reads have to accept integrations the POS settings UI never writes.
 */
export interface StoredPosExternalLocationMapping extends Omit<
  PosExternalLocationMapping,
  "integration"
> {
  integration: ExternalRmaIntegration;
}

export function isPosExternalLocationIntegration(
  integration: string,
): integration is PosExternalLocationIntegration {
  return (POS_EXTERNAL_LOCATION_INTEGRATIONS as readonly string[]).includes(
    integration,
  );
}

/**
 * Resolves the location an integration should stamp on the records it creates
 * for an in-store return. Returns undefined for non-POS returns and for POS
 * locations the merchant hasn't mapped, so callers keep their existing
 * fallbacks.
 */
export function getPosExternalLocationId({
  mappings,
  posLocationId,
  integration,
}: {
  mappings: StoredPosExternalLocationMapping[] | undefined;
  posLocationId: string | null | undefined;
  integration: PosExternalLocationIntegration;
}): string | undefined {
  if (!posLocationId) {
    return undefined;
  }
  const match = mappings?.find(
    (mapping) =>
      mapping.integration === integration &&
      mapping.posLocationId === posLocationId,
  );
  return match?.externalLocationId || undefined;
}
